/**
 * marketingSource.ts
 *
 * The one-line "which message produced this lead" label for leads created
 * through a marketing request-info link. Shared by the lead list's source cell
 * and the lead detail's Source Info, so the two can never word it differently.
 *
 *   campaign    "Spring Botox · SMS · Botox Package"
 *   automation  "New Patient Welcome · Email · step 3 · Botox Package"
 *   bare link   "Request-info link · Botox Package"
 *
 * Missing parts are dropped rather than rendered as blanks, and a campaign or
 * automation deleted since keeps its old name with " (deleted)" after it.
 */
import type { LeadMarketingSource } from '@/services/leads.service';

const DELETED_SUFFIX = ' (deleted)';

function withDeleted(name: string, deleted: boolean): string {
  if (!deleted || /\(deleted\)\s*$/i.test(name)) return name;
  return `${name}${DELETED_SUFFIX}`;
}

export function formatMarketingSource(
  source: LeadMarketingSource,
  options: { includeReferral?: boolean } = {},
): string {
  const resource = String(source.resourceName ?? '').trim();
  const parts: string[] = [];

  if (source.kind === 'link') {
    parts.push('Request-info link');
    if (resource) parts.push(resource);
  } else {
    const name = String(
      (source.kind === 'campaign' ? source.campaignName : source.automationName)
        ?? source.campaignName
        ?? source.automationName
        ?? '',
    ).trim();
    if (name) parts.push(withDeleted(name, source.deleted));
    if (source.channel === 'email') parts.push('Email');
    if (source.channel === 'sms') parts.push('SMS');
    const step = Number(source.stepOrder ?? 0);
    if (source.kind === 'automation' && Number.isFinite(step) && step > 0) parts.push(`step ${step}`);
    if (resource) parts.push(resource);
    // Nothing left to name the campaign by, but it was deleted: say so anyway.
    if (!name && source.deleted) parts.push('(deleted)');
  }

  if (options.includeReferral && source.referral) parts.push('Referral');
  return parts.join(' · ');
}
